import React, { useState, useEffect } from 'react';
import CurrencyInput from 'react-currency-input-field';
import PropTypes from 'prop-types';
import { Box, Button, Divider, TextField, Typography, useTheme } from '@mui/material';
import { Iconify } from 'src/components';
import { error, primary, grey } from 'src/theme/palette';
import { dispatch, useSelector } from '../../redux/store';
import { getPracticeTreatmentListV2 } from '../../redux/slices/practiceTreatment';
import { ErrorTextStyle, AmountInputStyle } from '../../styles/PaymentRefund';

// containerSx — optional override for the selected-treatment bar's own chrome.
// The Book Appointment step uses it so this bar IS the section panel instead of a
// second bordered box nested inside one. Omitted everywhere else, so the existing
// look is unchanged for all other call sites.
const TreatmentFlowComponent = ({ treatmentData, teethAmount, handleTreatment, amount, errorMessage, amountError, amountHandleChange, handleChangeTeeth, teethAmountError, starredTreatments, onTreatmentChipClick, containerSx }) => {
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const { treatmentsListV2 } = useSelector((state) => state.practiceTreatment);
    const theme = useTheme();
    const isLight = theme.palette.mode === 'light';
    const [decimalSeparator] = useState('.');
    const [thousandSeparator] = useState(',');
    const [inputType] = useState('text');
    const treatmentWhichNeedsTeethCount = ['Veneers', 'Composite Bonding'];
    const isTeethFieldNeeded = () => (treatmentWhichNeedsTeethCount?.includes(treatmentData) || treatmentsListV2?.results?.find(el => el.treatment_item.name === treatmentData)?.treatment_item?.treatment_category?.name === 'Implants');

    useEffect(() => {
        if (practiceId) {
            const data = {
                limit: 30,
                offset: 0,
            };
            dispatch(getPracticeTreatmentListV2(data));
        }
    }, [practiceId]);

    return (
        <>{treatmentData ?
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    mb: 2,
                    alignItems: 'flex-start',
                    p: 1.5,
                    width: '100%',
                    bgcolor: 'rgba(145, 158, 171, 0.04)',
                    borderRadius: '12px',
                    border: '1px solid rgba(145, 158, 171, 0.16)',
                    ...containerSx,
                }}
            >
                <Box sx={{ flex: '1 1 auto', minWidth: '120px' }}>
                    <Typography
                        sx={{
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            color: 'text.secondary',
                            mb: 0.5,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                        }}
                    >
                        Treatment name
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: '1rem',
                            fontWeight: 600,
                            color: 'text.primary',
                        }}
                    >
                        {treatmentData || ''}
                    </Typography>
                </Box>
                {(isTeethFieldNeeded()) && (
                    <>
                        <Divider orientation='vertical' flexItem sx={{ borderStyle: 'dashed', mx: 1 }} />
                        <Box sx={{ flex: '0 0 auto', minWidth: '100px' }}>
                            <Typography
                                sx={{
                                    fontSize: '0.75rem',
                                    fontWeight: 500,
                                    color: 'text.secondary',
                                    mb: 0.5,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                }}
                            >
                                Teeth amount
                            </Typography>
                            <Box data-field-name={'teethAmount'}>
                                <TextField
                                    size="small"
                                    onChange={handleChangeTeeth}
                                    value={teethAmount || ''}
                                    name="teethAmount"
                                    type="number"
                                    placeholder="0"
                                    sx={{
                                        width: '80px',
                                        '& .MuiOutlinedInput-root': {
                                            bgcolor: 'background.paper',
                                        },
                                    }}
                                />
                                {teethAmountError && (
                                    <Typography variant='p' sx={ErrorTextStyle}>
                                        Teeth amount should not be less than 1
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </>
                )}
                <Divider orientation='vertical' flexItem sx={{ borderStyle: 'dashed', mx: 1 }} />
                <Box sx={{ flex: '0 0 auto', minWidth: '120px' }}>
                    <Typography
                        sx={{
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            color: 'text.secondary',
                            mb: 0.5,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                        }}
                    >
                        Price
                    </Typography>
                    <Box data-field-name={'treatmentValue'}>
                        <CurrencyInput
                            name='treatmentValue'
                            value={amount}
                            decimalSeparator={decimalSeparator}
                            thousandSeparator={thousandSeparator}
                            onValueChange={(value, name) => amountHandleChange(value, name)}
                            className='currency-input'
                            decimalsLimit={2}
                            decimalScale={2}
                            inputType={inputType}
                            prefix={'£ '}
                            style={{
                                ...AmountInputStyle(amountError, 'appointment', isLight),
                                width: '100px',
                                padding: '8px 12px',
                                fontSize: '0.875rem',
                            }}
                            step={1}
                        />
                        {amountError && (
                            <Typography variant='p' sx={ErrorTextStyle}>
                                This field is required
                            </Typography>
                        )}
                    </Box>
                </Box>
                <Divider orientation='vertical' flexItem sx={{ borderStyle: 'dashed', mx: 1 }} />
                <Box sx={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', alignSelf: 'center' }}>
                    <Button
                        variant="text"
                        size="small"
                        onClick={handleTreatment}
                        startIcon={<Iconify icon="mdi:pencil-outline" width={16} height={16} />}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '0.8125rem',
                            color: '#00A76F',
                            px: 1.5,
                            py: 0.75,
                            borderRadius: '8px',
                            '&:hover': {
                                bgcolor: 'rgba(0, 167, 111, 0.08)',
                            },
                        }}
                    >
                        Edit
                    </Button>
                </Box>
            </Box>
            :
            <Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
                    <Button
                        variant="outlined"
                        onClick={handleTreatment}
                        startIcon={<Iconify icon="mdi:plus" width={18} height={18} />}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '0.875rem',
                            color: '#00A76F',
                            borderColor: '#00A76F',
                            px: 2,
                            py: 0.5,
                            height: '32px',
                            borderRadius: '50px',
                            '&:hover': {
                                bgcolor: 'rgba(0, 167, 111, 0.08)',
                                borderColor: '#007867',
                            },
                        }}
                    >
                        All Treatments
                    </Button>
                    {(!starredTreatments || starredTreatments.length === 0) && (
                        <Typography variant="caption" sx={{ color: 'text.disabled', ml: 1, alignSelf: 'center' }}>
                            Pin your favourite treatments to see them here
                        </Typography>
                    )}
                    {starredTreatments?.map((t) => {
                        const name = t.treatment_item?.name || t.name;
                        const isSelected = treatmentData === name;
                        let textColor = grey[400];
                        if (isSelected) {
                            textColor = primary.main;
                        } else if (isLight) {
                            textColor = grey[800];
                        }
                        return (
                            <Button
                                key={t.id}
                                variant="outlined"
                                onClick={() => onTreatmentChipClick?.(t)}
                                startIcon={
                                    <Iconify
                                        icon={isSelected ? 'bi:check-circle-fill' : 'material-symbols:circle'}
                                        width={20}
                                        height={20}
                                        sx={{ color: isSelected ? primary.main : grey[400] }}
                                    />
                                }
                                sx={{
                                    borderRadius: '50px',
                                    border: `1px solid ${isSelected ? primary.main : 'rgba(145, 158, 171, 0.32)'}`,
                                    m: '0 !important',
                                    p: '4px 12px 4px 5px',
                                    height: '32px',
                                    color: textColor,
                                    bgcolor: 'transparent',
                                    fontWeight: 400,
                                    textTransform: 'none',
                                    fontSize: '0.875rem',
                                    minWidth: 'auto',
                                    '&:hover': {
                                        bgcolor: 'rgba(145, 158, 171, 0.08)',
                                        borderColor: isSelected ? primary.main : 'rgba(145, 158, 171, 0.48)',
                                    },
                                }}
                            >
                                {name}
                            </Button>
                        );
                    })}
                </Box>
                {!!errorMessage && (
                    <Typography sx={{ color: error.main, fontSize: '14px', mt: 1 }}>
                        {errorMessage}
                    </Typography>
                )}
            </Box>}
        </>
    );
};

TreatmentFlowComponent.propTypes = {
    treatmentData: PropTypes.string,
    teethAmount: PropTypes.number,
    handleTreatment: PropTypes.func,
    amount: PropTypes.number,
    errorMessage: PropTypes.string,
    amountError: PropTypes.bool,
    amountHandleChange: PropTypes.func,
    handleChangeTeeth: PropTypes.func,
    teethAmountError: PropTypes.bool,
    starredTreatments: PropTypes.array,
    onTreatmentChipClick: PropTypes.func,
    containerSx: PropTypes.object,
};

TreatmentFlowComponent.defaultProps = {
    containerSx: undefined,
};

export default TreatmentFlowComponent;
